import type { Theme, SxProps } from '@mui/material/styles';
import type { ClassCodeLookupOption, CodeDescriptionLookupOption } from 'src/api/services/policy.service';

import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useBoolean } from 'minimal-shared/hooks';

import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import { alpha, useTheme } from '@mui/material/styles';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Table,
  Button,
  Tooltip,
  Collapse,
  TableRow,
  MenuItem,
  TextField,
  TableHead,
  TableCell,
  TableBody,
  IconButton,
  TableContainer,
} from '@mui/material';

import { formatDollarAmount } from '../../../../../shared/utils/policyUtils';

import type { UnitStatExposureDraftRow } from '../unit-stat-exposures-edit.helpers';

const editButtonSx = (theme: Theme): SxProps<Theme> => ({
  color: '#1C252E',
  fontFamily: theme.typography.button.fontFamily,
  fontWeight: theme.typography.fontWeightMedium,
  fontStyle: 'normal',
  fontSize: theme.typography.button.fontSize,
  lineHeight: theme.typography.button.lineHeight,
  letterSpacing: 0,
  textAlign: 'center',
  verticalAlign: 'middle',
  textTransform: 'none',
});

type UnitStatExposuresEditSplitRowProps = {
  splitCode: string;
  summary?: { splitIndicator: string | null; subjectPremium: number | null; modifiedPremium: number | null };
  existingRows: UnitStatExposureDraftRow[];
  newRows: UnitStatExposureDraftRow[];
  canDeleteSplit: boolean;
  errors: Record<string, string>;
  updateTypes: CodeDescriptionLookupOption[];
  classCodes: ClassCodeLookupOption[];
  onAddExposure: (splitCode: string) => void;
  onRemoveSplit: (splitCode: string) => void;
  onRemoveRow: (clientId: string) => void;
  onUpdateRow: (clientId: string, field: string, value: string) => void;
  onUpdateClassCode: (clientId: string, classCode: string) => void;
};

export default function UnitStatExposuresEditSplitRow({
  splitCode,
  summary,
  existingRows,
  newRows,
  canDeleteSplit,
  errors,
  updateTypes,
  classCodes,
  onAddExposure,
  onRemoveSplit,
  onRemoveRow,
  onUpdateRow,
  onUpdateClassCode,
}: Readonly<UnitStatExposuresEditSplitRowProps>) {
  const open = useBoolean(true);
  const theme = useTheme();

  // Re-expand this split's collapsed (unmountOnExit) section so a row it contains is actually
  // in the DOM and reachable by validateForSave's scroll-to-error behavior.
  useEffect(() => {
    const rowClientIds = [...existingRows, ...newRows].map((row) => row.clientId);
    const splitHasError = Object.keys(errors).some((errorKey) =>
      rowClientIds.some((clientId) => errorKey.startsWith(`${clientId}.`))
    );
    if (splitHasError) {
      open.onTrue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only errors changing should retrigger this; open/rows are read at effect time.
  }, [errors]);


  const isSubjectToExperienceMod = (classCode: string) =>
    classCodes.find((option) => option.classCode === classCode)?.subjectToExperienceMod ?? false;

  const getModTypeDisplay = (classCode: string) => {
    const option = classCodes.find((item) => item.classCode === classCode);
    if (!option) return '';
    return option.subjectToExperienceMod
      ? 'Subject to Experience Modification'
      : 'Not Subject to Experience Modification';
  };

  // Always resolve via the updateTypes lookup so the edit state matches the view state,
  // rather than trusting a possibly stale/blank description captured on the exposure snapshot.
  const getUpdateTypeDescription = (code: string) =>
    updateTypes.find((option) => option.code === code)?.description || code;

  const cellBottomSx = { borderBottom: '1px dashed var(--palette-divider)' };

  const renderExistingRow = (row: UnitStatExposureDraftRow) => (
    <TableRow sx={cellBottomSx} key={row.clientId} data-testid={`exposure-view-row-${row.clientId}`}>
      <TableCell>{row.modType ?? ''}</TableCell>
      <TableCell>{getUpdateTypeDescription(row.updateTypeCode)}</TableCell>
      <TableCell>{row.expModFactorEffectiveDate}</TableCell>
      <TableCell>{row.rateEffectiveDate}</TableCell>
      <TableCell>{row.expModFactor}</TableCell>
      <TableCell>
        {row.classCode}
        {row.classDescription ? ` - ${row.classDescription}` : ''}
      </TableCell>
      <TableCell>{row.exposure === '' ? '' : formatDollarAmount(Number(row.exposure))}</TableCell>
      <TableCell>{row.manualRate}</TableCell>
      <TableCell>{row.premium === '' ? '' : formatDollarAmount(Number(row.premium))}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', p: 0.5 }}>
        <Tooltip title="Delete exposure">
          <IconButton
            size="small"
            sx={{ color: '#637381' }}
            onClick={() => onRemoveRow(row.clientId)}
            data-testid={`delete-exposure-${row.clientId}`}
            aria-label="Delete exposure"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  const renderNewRow = (row: UnitStatExposureDraftRow) => {
    const rowIsSubjectToExperienceMod = isSubjectToExperienceMod(row.classCode);
    return (
      <TableRow
        key={row.clientId}
        data-testid={`exposure-edit-row-${row.clientId}`}
        sx={cellBottomSx}
      >
        <TableCell colSpan={10} sx={{ p: 2, backgroundColor: '#919EAB1F' }}>
          <span id={`new-exposure-form-${row.clientId}`} aria-hidden="true" />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <TextField
              select
              size="small"
              label="Class code"
              value={row.classCode}
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: 54 } }}
              error={Boolean(errors[`${row.clientId}.classCode`])}
              helperText={errors[`${row.clientId}.classCode`]}
              onChange={(event) => onUpdateClassCode(row.clientId, event.target.value)}
            >
              {classCodes.map((option) => (
                <MenuItem key={option.classCode} value={option.classCode}>
                  {option.classCode} - {option.classDescription}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Mod type"
              value={getModTypeDisplay(row.classCode)}
              disabled
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: 54 } }}
            />
            <TextField
              select
              size="small"
              label="Update type"
              value={row.updateTypeCode}
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: 54 } }}
              error={Boolean(errors[`${row.clientId}.updateTypeCode`])}
              helperText={errors[`${row.clientId}.updateTypeCode`]}
              onChange={(event) => onUpdateRow(row.clientId, 'updateTypeCode', event.target.value)}
            >
              {updateTypes
                .filter((option) => option.code === 'R' || option.code === 'P')
                .map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.description}
                  </MenuItem>
                ))}
            </TextField>
            <DatePicker
              label="Mod effective"
              format="MM/DD/YYYY"
              value={row.expModFactorEffectiveDate ? dayjs(row.expModFactorEffectiveDate, 'MM/DD/YYYY') : null}
              onChange={(value) =>
                onUpdateRow(
                  row.clientId,
                  'expModFactorEffectiveDate',
                  value?.isValid() ? value.format('MM/DD/YYYY') : ''
                )
              }
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: Boolean(errors[`${row.clientId}.expModFactorEffectiveDate`]),
                  helperText: errors[`${row.clientId}.expModFactorEffectiveDate`],
                  sx: { '& .MuiInputBase-root': { height: 54 } },
                },
              }}
            />
            <DatePicker
              label="Rate effective"
              format="MM/DD/YYYY"
              value={row.rateEffectiveDate ? dayjs(row.rateEffectiveDate, 'MM/DD/YYYY') : null}
              onChange={(value) =>
                onUpdateRow(row.clientId, 'rateEffectiveDate', value?.isValid() ? value.format('MM/DD/YYYY') : '')
              }
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: Boolean(errors[`${row.clientId}.rateEffectiveDate`]),
                  helperText: errors[`${row.clientId}.rateEffectiveDate`],
                  sx: { '& .MuiInputBase-root': { height: 54 } },
                },
              }}
            />
            <TextField
              size="small"
              label="Experience mod"
              value={row.expModFactor}
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: 54 } }}
              error={Boolean(errors[`${row.clientId}.expModFactor`])}
              helperText={errors[`${row.clientId}.expModFactor`]}
              onChange={(event) => onUpdateRow(row.clientId, 'expModFactor', event.target.value)}
              slotProps={{ htmlInput: { maxLength: 7, inputMode: 'decimal' } }}
            />
            <TextField
              size="small"
              label="Exposure"
              // Displays '0' as a visual placeholder when non-editable; the underlying draft
              // value stays empty so it is still persisted as unset for non-subject classes.
              value={rowIsSubjectToExperienceMod ? row.exposure : '0'}
              disabled={!rowIsSubjectToExperienceMod}
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: 54 } }}
              error={Boolean(errors[`${row.clientId}.exposure`])}
              helperText={errors[`${row.clientId}.exposure`]}
              onChange={(event) => onUpdateRow(row.clientId, 'exposure', event.target.value)}
              slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' } }}
            />
            <TextField
              size="small"
              label="Manual rate"
              value={row.manualRate}
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: 54 } }}
              error={Boolean(errors[`${row.clientId}.manualRate`])}
              helperText={errors[`${row.clientId}.manualRate`]}
              onChange={(event) => onUpdateRow(row.clientId, 'manualRate', event.target.value)}
              slotProps={{ htmlInput: { maxLength: 8, inputMode: 'decimal' } }}
            />
            <TextField
              size="small"
              label="Premium"
              value={row.premium}
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: 54 } }}
              error={Boolean(errors[`${row.clientId}.premium`])}
              helperText={errors[`${row.clientId}.premium`]}
              onChange={(event) => onUpdateRow(row.clientId, 'premium', event.target.value)}
              slotProps={{ htmlInput: { maxLength: 9 } }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Tooltip title="Delete exposure">
              <IconButton
                size="small"
                sx={{ color: '#637381' }}
                onClick={() => onRemoveRow(row.clientId)}
                data-testid={`delete-exposure-${row.clientId}`}
                aria-label="Delete exposure"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    );
  };


  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          {(existingRows.length > 0 || newRows.length > 0) && (
            <IconButton
              size="small"
              aria-label="expand row"
              onClick={open.onToggle}
              data-testid={`expand-button-${splitCode}`}
            >
              {open.value ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{summary?.splitIndicator ?? `Split ${splitCode}`}</TableCell>
        <TableCell>{formatDollarAmount(summary?.subjectPremium ?? 0)}</TableCell>
        <TableCell>{formatDollarAmount(summary?.modifiedPremium ?? 0)}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Button
            size="small"
            onClick={() => onAddExposure(splitCode)}
            data-testid={`add-exposure-${splitCode}`}
            startIcon={<AddIcon sx={{ color: '#2092BA' }} />}
            sx={editButtonSx(theme)}
          >
            Add exposure
          </Button>
          {canDeleteSplit && (
            <Tooltip title="Delete split">
              <IconButton
                size="small"
                sx={{ color: '#637381' }}
                onClick={() => onRemoveSplit(splitCode)}
                data-testid={`delete-split-${splitCode}`}
                aria-label="Delete split"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      {(existingRows.length > 0 || newRows.length > 0) && (
        <TableRow>
          <TableCell
            colSpan={5}
            sx={{
              p: 0,
              backgroundColor: open.value ? alpha(theme.palette.grey[500], 0.4) : 'inherit',
            }}
          >
            <Collapse in={open.value} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <TableContainer sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}>
                  <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                    {existingRows.length > 0 && (
                      <TableHead>
                        <TableRow>
                          <TableCell>Mod type</TableCell>
                          <TableCell>Update type</TableCell>
                          <TableCell>Mod effective</TableCell>
                          <TableCell>Rate effective</TableCell>
                          <TableCell>Experience mod</TableCell>
                          <TableCell>Class code</TableCell>
                          <TableCell>Exposure</TableCell>
                          <TableCell>Manual rate</TableCell>
                          <TableCell>Premium</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                    )}
                    <TableBody>
                      {existingRows.map(renderExistingRow)}
                      {newRows.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ p: 0 }}>
                            <Box sx={{ py: 2 }}>
                              <TableContainer
                                sx={{ backgroundColor: 'background.paper'}}
                              >
                                <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                                  <TableBody>{newRows.map(renderNewRow)}</TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
